import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import DataTable from '../common/DataTable';
import { formatCurrency, formatDate, getAgeing, today, firstOfMonth } from '../../utils/helpers';

// Item weight (Kg) × quantity, shown on the item/party/HSN purchase reports.
const fmtWt = (v) => `${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })} kg`;

const reportTypes = [
  { key: 'register', label: 'Purchase Register' },
  { key: 'partywise', label: 'Party-wise Purchase' },
  { key: 'itemwise', label: 'Item-wise Purchase' },
  { key: 'gst', label: 'GST Purchase Register' },
  { key: 'itemHsn', label: 'Purchase Item Wise HSN Report' },
  { key: 'paymentAdj', label: 'Payment Adjustment Report' },
  { key: 'payables', label: 'Outstanding Payables' },
  { key: 'returns', label: 'Purchase Return Register' },
];

export default function PurchaseReports() {
  const { state } = useApp();
  const [report, setReport] = useState('register');
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());

  const filtered = useMemo(() => {
    let bills = state.purchaseBills;
    if (startDate) bills = bills.filter(b => b.date >= startDate);
    if (endDate) bills = bills.filter(b => b.date <= endDate);
    return bills;
  }, [state.purchaseBills, startDate, endDate]);

  const renderReport = () => {
    switch (report) {
      case 'register':
        return <DataTable columns={[
          { key: 'entryNo', label: 'Entry No', sortable: true },
          { key: 'billNo', label: 'Bill No' },
          { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
          { key: 'partyId', label: 'Supplier', render: (v) => state.parties.find(p => p.id === v)?.name, exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.name || '' },
          { key: 'gstin', label: 'GST No', render: (_, r) => state.parties.find(p => p.id === r.partyId)?.gstin || '-', exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.gstin || '' },
          { key: 'pstate', label: 'State', render: (_, r) => state.parties.find(p => p.id === r.partyId)?.state || '-', exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.state || '' },
          { key: 'pstatecode', label: 'State Code', render: (_, r) => state.parties.find(p => p.id === r.partyId)?.stateGstCode || '-', exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.stateGstCode || '' },
          { key: 'taxable', label: 'Taxable', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'totalTax', label: 'Tax', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'grandTotal', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
          { key: 'status', label: 'Status', render: (v) => <span className={`badge ${v === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{v}</span> },
        ]} data={filtered} searchFields={['entryNo', 'billNo']} />;

      case 'partywise': {
        const map = {};
        filtered.forEach(bill => {
          const name = state.parties.find(p => p.id === bill.partyId)?.name || 'Unknown';
          if (!map[name]) map[name] = { party: name, count: 0, weight: 0, total: 0 };
          map[name].count++;
          map[name].total += bill.grandTotal || 0;
          (bill.items || []).forEach(li => {
            const item = state.items.find(i => i.id === li.itemId);
            map[name].weight += (Number(li.qty) || 0) * (Number(item?.weight) || 0);
          });
        });
        return <DataTable columns={[
          { key: 'party', label: 'Supplier', sortable: true },
          { key: 'count', label: 'Bills', align: 'right' },
          { key: 'weight', label: 'Weight (Kg)', align: 'right', sortable: true, render: (v) => fmtWt(v), exportValue: (r) => r.weight },
          { key: 'total', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={Object.values(map).sort((a, b) => b.total - a.total)} searchFields={['party']}
          totals={(rows) => ({
            count: rows.reduce((s, r) => s + r.count, 0),
            weight: fmtWt(rows.reduce((s, r) => s + r.weight, 0)),
            total: formatCurrency(rows.reduce((s, r) => s + r.total, 0)),
          })} />;
      }

      case 'itemwise': {
        const map = {};
        filtered.forEach(bill => {
          bill.items?.forEach(li => {
            const item = state.items.find(i => i.id === li.itemId);
            const name = item?.name || 'Unknown';
            if (!map[name]) map[name] = { item: name, qty: 0, weight: 0, total: 0 };
            map[name].qty += Number(li.qty) || 0;
            map[name].weight += (Number(li.qty) || 0) * (Number(item?.weight) || 0);
            map[name].total += li.netAmount || 0;
          });
        });
        return <DataTable columns={[
          { key: 'item', label: 'Item', sortable: true },
          { key: 'qty', label: 'Qty', align: 'right' },
          { key: 'weight', label: 'Weight (Kg)', align: 'right', sortable: true, render: (v) => fmtWt(v), exportValue: (r) => r.weight },
          { key: 'total', label: 'Total', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={Object.values(map).sort((a, b) => b.total - a.total)} searchFields={['item']}
          totals={(rows) => ({
            qty: rows.reduce((s, r) => s + r.qty, 0),
            weight: fmtWt(rows.reduce((s, r) => s + r.weight, 0)),
            total: formatCurrency(rows.reduce((s, r) => s + r.total, 0)),
          })} />;
      }

      case 'gst':
        return <DataTable columns={[
          { key: 'entryNo', label: 'Entry No' },
          { key: 'date', label: 'Date', render: (v) => formatDate(v) },
          { key: 'partyId', label: 'Supplier', render: (v) => state.parties.find(p => p.id === v)?.name, exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.name || '' },
          { key: 'billNo', label: 'Supplier Bill No', render: (v) => v || '-' },
          { key: 'billDate', label: 'Bill Date', render: (v) => v ? formatDate(v) : '-' },
          { key: 'gstin', label: 'GST No', render: (_, r) => state.parties.find(p => p.id === r.partyId)?.gstin || '-', exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.gstin || '' },
          { key: 'taxable', label: 'Taxable', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'cgst', label: 'CGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'sgst', label: 'SGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'igst', label: 'IGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'grandTotal', label: 'Total', align: 'right', render: (v) => formatCurrency(v) },
        ]} data={filtered} searchFields={['entryNo', 'billNo']} />;

      case 'itemHsn': {
        const rows = [];
        filtered.forEach(bill => {
          (bill.items || []).forEach(li => {
            const item = state.items.find(i => i.id === li.itemId);
            const taxAmt = Number(li.taxAmount) || 0;
            const inter = bill.isInterState;
            rows.push({
              itemName: item?.name || li.itemName || 'Unknown',
              billDate: bill.billDate || bill.date,
              billNo: bill.billNo || bill.entryNo,
              hsn: item?.hsnCode || '-',
              qty: Number(li.qty) || 0,
              uom: item?.unit || li.unit || '-',
              weight: (Number(li.qty) || 0) * (Number(item?.weight) || 0),
              taxable: Number(li.taxableAmount) || 0,
              sgst: inter ? 0 : taxAmt / 2,
              cgst: inter ? 0 : taxAmt / 2,
              igst: inter ? taxAmt : 0,
              total: Number(li.netAmount) || 0,
            });
          });
        });
        return <DataTable columns={[
          { key: 'itemName', label: 'Item Name', sortable: true },
          { key: 'billDate', label: 'Bill Date', render: (v) => v ? formatDate(v) : '-' },
          { key: 'billNo', label: 'Bill No' },
          { key: 'hsn', label: 'HSN No' },
          { key: 'qty', label: 'Qty', align: 'right' },
          { key: 'uom', label: 'UOM' },
          { key: 'weight', label: 'Weight (Kg)', align: 'right', render: (v) => fmtWt(v), exportValue: (r) => r.weight },
          { key: 'taxable', label: 'Taxable Amount', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'sgst', label: 'SGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'cgst', label: 'CGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'igst', label: 'IGST', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'total', label: 'Total', align: 'right', render: (v) => formatCurrency(v) },
        ]} data={rows} searchFields={['itemName', 'billNo', 'hsn']} />;
      }

      case 'paymentAdj': {
        const data = state.payments
          .filter(p => p.againstBill)
          .filter(p => (!startDate || p.date >= startDate) && (!endDate || p.date <= endDate))
          .map(p => {
            const bill = state.purchaseBills.find(b => b.entryNo === p.againstBill);
            return { paymentNo: p.paymentNo, date: p.date, billNo: p.againstBill, billDate: bill?.billDate || bill?.date || '', amount: p.amount };
          });
        return <DataTable columns={[
          { key: 'paymentNo', label: 'Payment Entry No', sortable: true },
          { key: 'date', label: 'Date', render: (v) => formatDate(v) },
          { key: 'billNo', label: 'Bill No' },
          { key: 'billDate', label: 'Bill Date', render: (v) => v ? formatDate(v) : '-' },
          { key: 'amount', label: 'Amount', align: 'right', sortable: true, render: (v) => formatCurrency(v) },
        ]} data={data} searchFields={['paymentNo', 'billNo']} />;
      }

      case 'payables': {
        const data = state.purchaseBills
          .filter(b => b.status !== 'Paid')
          .map(b => ({
            ...b,
            supplierName: state.parties.find(p => p.id === b.partyId)?.name,
            outstanding: (b.grandTotal || 0) - (b.paidAmount || 0),
            ageing: getAgeing(b.date),
          }));
        return <DataTable columns={[
          { key: 'entryNo', label: 'Bill No' },
          { key: 'date', label: 'Date', render: (v) => formatDate(v) },
          { key: 'supplierName', label: 'Supplier' },
          { key: 'grandTotal', label: 'Bill Amt', align: 'right', render: (v) => formatCurrency(v) },
          { key: 'paidAmount', label: 'Paid', align: 'right', render: (v) => formatCurrency(v || 0) },
          { key: 'outstanding', label: 'Outstanding', align: 'right', render: (v) => <span className="text-red-600 font-semibold">{formatCurrency(v)}</span> },
          { key: 'ageing', label: 'Ageing', render: (v) => <span className={`badge ${v === '0-30' ? 'badge-success' : v === '31-60' ? 'badge-warning' : 'badge-danger'}`}>{v} days</span> },
        ]} data={data} searchFields={['entryNo', 'supplierName']} />;
      }

      case 'returns':
        return <DataTable columns={[
          { key: 'returnNo', label: 'Return No' },
          { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: (r) => formatDate(r.date) },
          { key: 'partyId', label: 'Supplier', render: (v) => state.parties.find(p => p.id === v)?.name, exportValue: (r) => state.parties.find(p => p.id === r.partyId)?.name || '' },
          { key: 'grandTotal', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
        ]} data={state.purchaseReturns} searchFields={['returnNo']} />;

      default: return null;
    }
  };

  return (
    <div>
      <h1 className="page-title mb-6">Purchase Reports</h1>
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">Report Type</label>
            <select className="input" value={report} onChange={e => setReport(e.target.value)}>
              {reportTypes.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">From</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="card">{(() => {
        const el = renderReport();
        return el && el.type === DataTable ? React.cloneElement(el, { exportFilename: `Purchase_${report}` }) : el;
      })()}</div>
    </div>
  );
}
